// deps
import React, { memo } from "react";
import { Image } from "@swiggy-private/dls-web-core/dist/components/Image";
import { ToggleSwitch, format } from "@swiggy-private/core-ui";
import { ElementVisibilitySensor } from "@swiggy-private/dls-web-core/dist/components/ElementVisibilitySensor";
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";
import { isEmpty } from "@swiggy-private/js-utils";
import { IColors } from "@swiggy-private/dls-web-common/dist/components/types.internal";

// constants
import { AMOUNT_FORMAT, REWARD_LOGO_HEIGHT_WIDTH } from "@dineout-cart-ui/constants/index";

// interfaces
import { IRewardsWidgetContentProps } from "@dineout-cart-ui/interfaces/Cart";

// test IDs
import { REWARDS_WIDGET_TEST_IDS } from "@dineout-cart-ui/helpers/TestIds";

// components
import ArrowRightIcon from "./ArrowRightIcon";
import TopArrowIcon from "./TopArrowIcon";
import { RewardTitleWithExtraInfo } from "./RewardTitleWithExtraInfo";

// styles
import {
    RewardsWidgetContainer,
    RewardsWidgetWithNudgeWrapper,
    RewardsDineCashContainer,
    RewardLogo,
    RedeemableStyledText,
    UseRewardContainer,
    ApplyRewardContainer,
    EarnRewardsContainer,
    EarnedReward,
    RedeemableContainerWithArrow,
    TopArrowIconContainer,
    BottomNudgeTextCalloutContainer,
} from "./styles";

const ARROW_ICON_DISABLED_COLOR = "rgba(255, 255, 255, 0.3)";
const ARROW_ICON_DEFAULT_COLOR = "#009062";

export const RewardsWidgetContent = memo(
    ({
        backgroundImage,
        earnedRewards,
        earnRewardsTitle,
        disableEarnRewardsTitle,
        logo,
        redeemableRewards,
        subtitle,
        title,
        extraInfoText,
        isBoosterActive,
        isToggleDisabled = false,
        isSelected,
        isScenesFlow,
        bottomNudgeTextCallout,
        toggleThumbColor,
        onToggleSwitch,
        onCashBackEarnedImpression,
        onCashBackToggleImpression,
    }: IRewardsWidgetContentProps): React.ReactElement => {
        const hasBottomNudge = !isEmpty(bottomNudgeTextCallout?.text);
        const redeemableAmountColor: keyof IColors = isToggleDisabled ? "text_disabled" : "text_Highest_Emphasis";

        const content = (
            <RewardsWidgetContainer
                backgroundImage={isBoosterActive ? "" : backgroundImage}
                booster={isBoosterActive}
                data-testid={REWARDS_WIDGET_TEST_IDS.CONTAINER}
                isScenesFlow={isScenesFlow}
                skipOuterMargin={hasBottomNudge}
            >
                <ElementVisibilitySensor senseOnce onVisibilityChange={onCashBackEarnedImpression}>
                    <EarnRewardsContainer isTitle={Boolean(title)}>
                        <Text
                            variant={title ? `V2.body3.neutral.medium` : `V2.body3.neutral.bold`}
                            color={disableEarnRewardsTitle ? "text_disabled" : "text_High_Emphasis"}
                            data-testid={REWARDS_WIDGET_TEST_IDS.EARNED_REWARDS_TITLE}
                        >
                            {earnRewardsTitle}
                        </Text>
                        <EarnedReward>
                            {isBoosterActive ? (
                                <TopArrowIcon />
                            ) : (
                                <ArrowRightIcon
                                    color={
                                        disableEarnRewardsTitle ? ARROW_ICON_DISABLED_COLOR : ARROW_ICON_DEFAULT_COLOR
                                    }
                                />
                            )}
                            <Text
                                variant="V2.body2.neutral.bold"
                                color={disableEarnRewardsTitle ? "text_disabled" : "text_Highest_Emphasis"}
                                data-testid={REWARDS_WIDGET_TEST_IDS.EARNED_REWARDS_AMOUNT}
                            >
                                {format(AMOUNT_FORMAT, earnedRewards?.toString() ?? "0")}
                            </Text>
                        </EarnedReward>
                    </EarnRewardsContainer>
                </ElementVisibilitySensor>
                <UseRewardContainer>
                    {!isEmpty(title) && (
                        <>
                            <RewardsDineCashContainer>
                                {!isBoosterActive && (
                                    <RewardLogo disabled={isToggleDisabled}>
                                        <Image
                                            src={logo}
                                            height={REWARD_LOGO_HEIGHT_WIDTH}
                                            width={REWARD_LOGO_HEIGHT_WIDTH}
                                        />
                                    </RewardLogo>
                                )}
                                <RewardTitleWithExtraInfo
                                    title={title as string}
                                    subtitle={subtitle}
                                    extraInfoText={extraInfoText}
                                    isBoosterActive={isBoosterActive}
                                    isToggleDisabled={isToggleDisabled}
                                />
                            </RewardsDineCashContainer>
                            <ApplyRewardContainer>
                                <ElementVisibilitySensor senseOnce onVisibilityChange={onCashBackToggleImpression}>
                                    <ToggleSwitch
                                        selected={isSelected}
                                        isDisabled={isToggleDisabled}
                                        onClick={isToggleDisabled ? undefined : onToggleSwitch}
                                        activeColor="special_primary"
                                        thumbColor={toggleThumbColor}
                                        data-testid={REWARDS_WIDGET_TEST_IDS.TOGGLE_SWITCH}
                                    />
                                </ElementVisibilitySensor>
                                <RedeemableContainerWithArrow>
                                    {isBoosterActive && (
                                        <TopArrowIconContainer>
                                            <TopArrowIcon />
                                        </TopArrowIconContainer>
                                    )}
                                    <RedeemableStyledText
                                        variant="V2.body2.neutral.bold"
                                        color={redeemableAmountColor}
                                        data-testid={REWARDS_WIDGET_TEST_IDS.REDEEMABLE_REWARDS_AMOUNT}
                                    >
                                        {format(AMOUNT_FORMAT, redeemableRewards?.toString() ?? "0")}
                                    </RedeemableStyledText>
                                </RedeemableContainerWithArrow>
                            </ApplyRewardContainer>
                        </>
                    )}
                </UseRewardContainer>
            </RewardsWidgetContainer>
        );

        if (!hasBottomNudge) {
            return content;
        }

        return (
            <RewardsWidgetWithNudgeWrapper booster={isBoosterActive} isScenesFlow={isScenesFlow}>
                {content}
                {bottomNudgeTextCallout ? (
                    <BottomNudgeTextCalloutContainer
                        backgroundColor={bottomNudgeTextCallout.backgroundColor}
                        data-testid={REWARDS_WIDGET_TEST_IDS.BOTTOM_NUDGE_CALLOUT}
                    >
                        <Text variant="V2.caption.neutral.medium" color={bottomNudgeTextCallout.textColor} maxLines={2}>
                            {bottomNudgeTextCallout.text}
                        </Text>
                    </BottomNudgeTextCalloutContainer>
                ) : null}
            </RewardsWidgetWithNudgeWrapper>
        );
    },
);

RewardsWidgetContent.displayName = "RewardsWidgetContent";
